import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #imageSizeFrame = 0;
  #imageResizeObserver;
  #imageMutationObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Card widths change with density, filters, pagination and viewport size.
    this.#imageResizeObserver = new ResizeObserver(this.#scheduleImageSizes);
    this.#imageResizeObserver.observe(this);
    this.#imageMutationObserver = new MutationObserver(this.#scheduleImageSizes);
    this.#imageMutationObserver.observe(this, { childList: true, subtree: true });
    this.#scheduleImageSizes();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#imageResizeObserver?.disconnect();
    this.#imageMutationObserver?.disconnect();
    cancelAnimationFrame(this.#imageSizeFrame);
    this.#imageSizeFrame = 0;
  }

  #scheduleImageSizes = () => {
    if (this.#imageSizeFrame) return;
    this.#imageSizeFrame = requestAnimationFrame(() => {
      this.#imageSizeFrame = 0;
      if (!this.isConnected) return;

      // Read dimensions together before writing attributes to avoid layout churn.
      const galleries = [...this.querySelectorAll('.card-gallery')].map((gallery) => ({
        gallery,
        width: Math.ceil(gallery.getBoundingClientRect().width),
      }));
      for (const { gallery, width } of galleries) {
        if (width <= 0) continue;
        for (const image of gallery.querySelectorAll('img')) {
          const sizes = image.loading === 'lazy' ? `auto, ${width}px` : `${width}px`;
          if (image.sizes !== sizes) image.sizes = sizes;
        }
      }
    });
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#scheduleImageSizes();
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
